import React, { useEffect, useState } from 'react';
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  Dropdown,
  DropdownOverlay,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectInput,
  Spinner,
  Text,
} from '@razorpay/blade/components';

import { useOtSalaryComponents, useBulkUpdateOtBehaviour } from './hooks';
import type { OtSalaryComponent } from './hooks/useOtSalaryComponents';
import type { BulkUpdateOtBehaviourComponentInput } from 'api/gql-utils/overtime/types';

const SELECT_ALL_VALUE = '__select_all__';

interface EditSalaryComponentsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  activeMultiplierCount: number;
  onSuccess?: () => void;
}

export const computeDiff = (
  components: OtSalaryComponent[],
  selectedIds: Array<string>,
): BulkUpdateOtBehaviourComponentInput[] => {
  const changes: BulkUpdateOtBehaviourComponentInput[] = [];
  for (const comp of components) {
    const isNowSelected = selectedIds.includes(comp.id);
    if (isNowSelected !== comp.includeInOtWages) {
      changes.push({
        componentId: comp.id,
        includeInOtWages: isNowSelected,
        componentSource: comp.componentSource,
      });
    }
  }
  return changes;
};

export const EditSalaryComponentsModal = ({
  isOpen,
  onDismiss,
  activeMultiplierCount,
  onSuccess,
}: EditSalaryComponentsModalProps) => {
  const { data: components = [], isLoading } = useOtSalaryComponents();
  const { mutate: bulkUpdate, isLoading: isUpdating } = useBulkUpdateOtBehaviour();

  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && components.length > 0) {
      setSelectedIds(components.filter((c) => c.includeInOtWages).map((c) => c.id));
      setShowConfirm(false);
    }
  }, [isOpen, components]);

  const allSelected = components.length > 0 && selectedIds.length === components.length;

  const handleComponentChange = ({ values }: { values: string[] }) => {
    const hasSelectAll = values.includes(SELECT_ALL_VALUE);
    const filteredValues = values.filter((v) => v !== SELECT_ALL_VALUE);

    if (hasSelectAll) {
      if (allSelected) {
        // All were selected and __select_all__ is still in values — individual item was deselected
        setSelectedIds(filteredValues);
      } else {
        // "Select All" was just clicked — select all
        setSelectedIds(components.map((c) => c.id));
      }
    } else if (allSelected && filteredValues.length === components.length) {
      // "Select All" was removed while all components remain — user clicked it to deselect all
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredValues ?? []);
    }
  };

  const handleSaveClick = () => {
    const changes = computeDiff(components, selectedIds);
    if (changes.length === 0) {
      onSuccess?.();
      return;
    }
    setShowConfirm(true);
  };

  const submitChanges = () => {
    const changes = computeDiff(components, selectedIds);
    bulkUpdate(changes, {
      onSuccess: () => {
        setShowConfirm(false);
        onSuccess?.();
      },
    });
  };

  const handleConfirm = () => {
    submitChanges();
  };

  if (showConfirm) {
    return (
      <Modal
        isOpen={true}
        onDismiss={() => setShowConfirm(false)}
        size="small"
        accessibilityLabel="Confirm salary component update">
        <ModalHeader title="Update salary components?" />
        <ModalBody>
          <Text size="medium" color="surface.text.gray.muted">
            This will update the calculation basis for {activeMultiplierCount} active multiplier
            formula{activeMultiplierCount !== 1 ? 's' : ''}. Flat rate formulas are not affected.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" justifyContent="flex-end" gap="spacing.4">
            <Button variant="tertiary" onClick={() => setShowConfirm(false)}>
              Go Back
            </Button>
            <Button variant="primary" isLoading={isUpdating} onClick={handleConfirm}>
              Update Components
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="small"
      accessibilityLabel="Edit overtime salary components">
      <ModalHeader title="Edit overtime salary components" />
      <ModalBody>
        <Text size="medium" color="surface.text.gray.muted" marginBottom="spacing.5">
          These components form the base for all multiplier-based overtime formulas.
        </Text>
        {isLoading ? (
          <Box display="flex" justifyContent="center" paddingY="spacing.6">
            <Spinner accessibilityLabel="Loading salary components" />
          </Box>
        ) : (
          <Dropdown selectionType="multiple">
            <SelectInput
              label="Salary components"
              placeholder="Select components"
              onChange={handleComponentChange}
              value={allSelected ? [...selectedIds, SELECT_ALL_VALUE] : selectedIds}
            />
            <DropdownOverlay>
              <ActionList>
                <ActionListItem
                  key={SELECT_ALL_VALUE}
                  title="Select All"
                  value={SELECT_ALL_VALUE}
                  isSelected={allSelected}
                />
                {components.map((comp) => (
                  <ActionListItem
                    key={comp.id}
                    title={comp.name}
                    description={comp.description ?? undefined}
                    value={comp.id}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>
        )}
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.4">
          <Button variant="secondary" onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveClick}
            isDisabled={selectedIds.length === 0 || isLoading}
            isLoading={isUpdating}>
            Save
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
